// IndexedDB utility for caching calendar data

const DB_NAME = "NepaliCalendarDB";
const DB_VERSION = 1;
const STORE_NAME = "calendarData";

interface CalendarEntry {
  key: string; // format: "year-month" e.g., "2082-7"
  data: any;
  timestamp: number;
}

export class CalendarIndexedDB {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error("Failed to open IndexedDB"));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, {
            keyPath: "key",
          });
          objectStore.createIndex("timestamp", "timestamp", { unique: false });
        }
      };
    });
  }

  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init();
    }
    if (!this.db) {
      throw new Error("IndexedDB not initialized");
    }
    return this.db;
  }

  async get(year: number, month: number): Promise<any | null> {
    try {
      const db = await this.ensureDB();
      const key = `${year}-${month}`;

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], "readonly");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(key);

        request.onsuccess = () => {
          const result = request.result;
          if (result) {
            resolve(result.data);
          } else {
            resolve(null);
          }
        };

        request.onerror = () => {
          reject(new Error("Failed to get data from IndexedDB"));
        };
      });
    } catch (error) {
      console.error("Error getting data from IndexedDB:", error);
      return null;
    }
  }

  async set(year: number, month: number, data: any): Promise<void> {
    try {
      const db = await this.ensureDB();
      const key = `${year}-${month}`;
      const entry: CalendarEntry = {
        key,
        data,
        timestamp: Date.now(),
      };

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], "readwrite");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put(entry);

        request.onsuccess = () => resolve();

        request.onerror = () => {
          reject(new Error("Failed to save data to IndexedDB"));
        };
      });
    } catch (error) {
      console.error("Error saving data to IndexedDB:", error);
    }
  }

  async clear(): Promise<void> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], "readwrite");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => resolve();
        request.onerror = () => reject(new Error("Failed to clear IndexedDB"));
      });
    } catch (error) {
      console.error("Error clearing IndexedDB:", error);
    }
  }

  async getAllYears(): Promise<number[]> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], "readonly");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.getAll();

        request.onsuccess = () => {
          const entries: CalendarEntry[] = request.result;
          const years = new Set<number>();
          entries.forEach((entry) => {
            const [year] = entry.key.split("-").map(Number);
            years.add(year);
          });
          resolve(Array.from(years));
        };

        request.onerror = () =>
          reject(new Error("Failed to get years from IndexedDB"));
      });
    } catch (error) {
      console.error("Error getting years from IndexedDB:", error);
      return [];
    }
  }
}

export const calendarDB = new CalendarIndexedDB();
